import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  Typography,
} from 'antd';
import { tiposDatosEnum, tiposDatosFormulaEnum } from 'views/Tramites/GPM//enums';
import {
  toMoment,
  listToString,
  capitalize,
  getDistinctValues,
} from 'utils/normalizers';
import Select from 'components/Select';
import formatValues, { DATETIME_FRONTEND_FORMAT, DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { logger } from 'utils/debug';
import styled from 'styled-components';
import HTTP from 'utils/api';
import { useSelector } from 'react-redux';
import NumericInput from 'components/NumericInput';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import { deleteComplementoDeTramite, patchComplementoDeTramite, postComplementoDeTramite } from 'api/tramites/complementosDeTramites';

const tiposDatosNumericos = [
  tiposDatosEnum.ENTERO,
  tiposDatosFormulaEnum.ENTERO,
  tiposDatosEnum.DECIMAL,
  tiposDatosFormulaEnum.DECIMAL,
];

const tiposDatosSeleccion = [tiposDatosEnum.SELECCION_UNICA, tiposDatosEnum.SELECCION_MULTIPLE];

const tiposDatosTexto = [tiposDatosEnum.TEXTO_LARGO, tiposDatosEnum.TEXTO_CORTO];

/**
 * @param {object} values
 * @param {object[]} variables
 * @param {number} variables.id
 * @param {number} variables.nombre_de_variable
 * @param {number} variables.tipo_de_variable
 * @param {string|number} prename
 * @returns object[]
 */
export const valuesToVariables = (values, variables, prename) => variables.map((variable) => {
  let valor = prename
    ? values[prename][variable.nombre_de_variable] : values[variable.nombre_de_variable];
  if (variable.tipo_de_variable === tiposDatosEnum.RANGO_DE_FECHAS) {
    const [fechaInicial, fechaFinal] = valor;
    valor = fechaFinal.diff(fechaInicial, 'days');
  }
  return ({
    id: variable.id,
    nombre_de_variable: variable.nombre_de_variable,
    valor,
  });
});

/**
 * @param {object} values
 * @param {number[]} variables
 * @param {object[]} catalogoDeVariables
 * @param {number} catalogoDeVariables.id
 * @param {string} catalogoDeVariables.nombre_de_variable
 * @param {number} catalogoDeVariables.tipo_de_variable
 * @returns object[]
 */
export const valuesToVariablesComplementos = (
  values,
  variables,
  catalogoDeVariables,
  tramite,
) => variables.map((v) => catalogoDeVariables.find((e) => e.id === v)).map(({
  id,
  tipo_de_variable,
  es_catalogo_interno,
  nombre_de_variable,
}) => {
  if (tipo_de_variable === tiposDatosEnum.TABLA) return false;

  const formValue = values[nombre_de_variable];
  if (formValue === null || formValue === undefined) return null;
  const output = {
    variable: id,
    valores_por_variables_de_seleccion: [],
    ids_catalogo: [],
    tramite,
  };
  if (tipo_de_variable === tiposDatosEnum.RANGO_DE_FECHAS) {
    output.valor = formValue.map((e) => e.format(DATE_FRONTEND_FORMAT)).join();
  } else if (tipo_de_variable === tiposDatosEnum.FECHA) {
    output.valor = formValue.format(DATE_FRONTEND_FORMAT);
  } else if (tipo_de_variable === tiposDatosEnum.FECHA_HORA) {
    output.valor = formValue.format(DATETIME_FRONTEND_FORMAT);
  } else if (tipo_de_variable === tiposDatosEnum.BOOLEAN) {
    output.valor = formValue ? 'Sí' : 'No';
  } else if (tipo_de_variable === tiposDatosEnum.SELECCION_MULTIPLE) {
    if (es_catalogo_interno) {
      output.ids_catalogo = formValue.map((val) => val.split('__')[0]);
      output.valor = listToString(formValue.map((val) => val.split('__')[1]));
    } else {
      output.valores_por_variables_de_seleccion = formValue.map((e) => e.split('__')[0]);
      output.valor = listToString(formValue.map((e) => e.split('__')[1]));
    }
  } else if (tipo_de_variable === tiposDatosEnum.SELECCION_UNICA) {
    if (es_catalogo_interno) {
      [output.ids_catalogo, output.valor] = formValue.split('__');
      output.ids_catalogo = [output.ids_catalogo];
    } else {
      output.valores_por_variables_de_seleccion = [formValue.split('__')[0]];
      [, output.valor] = formValue.split('__');
    }
  } else {
    output.valor = formValue.toString();
  }
  output.ids_catalogo = output.ids_catalogo.join(',');
  output.valores_por_variables_de_seleccion = output.valores_por_variables_de_seleccion
    .map((e) => parseInt(e, 10));
  return output;
}).filter((v) => v);

const variableToFormValue = (
  { valor, valores_por_variables_de_seleccion, ids_catalogo: _ids_catalogo = '' },
  { tipo_de_variable, es_catalogo_interno },
) => {
  try {
    const ids_catalogo = _ids_catalogo?.split(',').map((id) => parseInt(id, 10)) || [];
    if (tipo_de_variable === tiposDatosEnum.RANGO_DE_FECHAS) {
      return valor.split(',').map((val) => toMoment(val, [DATE_FRONTEND_FORMAT]));
    }
    if (tipo_de_variable === tiposDatosEnum.FECHA) {
      return toMoment(valor, [DATE_FRONTEND_FORMAT]);
    }
    if (tipo_de_variable === tiposDatosEnum.FECHA_HORA) {
      return toMoment(valor, [DATETIME_FRONTEND_FORMAT]);
    }
    if (tipo_de_variable === tiposDatosEnum.BOOLEAN) return /si|sí|1/i.test(valor);
    if (tipo_de_variable === tiposDatosEnum.SELECCION_UNICA) {
      return `${es_catalogo_interno ? ids_catalogo : valores_por_variables_de_seleccion[0]}__${valor}`;
    }
    if (tipo_de_variable === tiposDatosEnum.SELECCION_MULTIPLE) {
      const valores = valor.split(/,|y/i).map((e) => e.trim());
      return (es_catalogo_interno ? ids_catalogo : valores_por_variables_de_seleccion)
        .map((id, idx) => `${id}__${valores[idx]}`);
    }
    return valor;
  } catch (error) {
    logger(error);
  }
  return null;
};

/**
 * @param {object[]} variables
 * @param {number} variables.valor
 * @param {number} variables.variable
 * @param {object[]} catalogoDeVariables
 * @param {number} catalogoDeVariables.id
 * @param {number} catalogoDeVariables.nombre_de_variable
 * @returns object
 */
export const complementosToValues = (complementos, catalogoDeVariables) => {
  const entries = complementos.map((complemento) => {
    const variable = catalogoDeVariables.find((v) => v.id === complemento.variable);
    if (!variable || variable.tipo_de_variable === tiposDatosEnum.TABLA) return [];
    return [
      variable.nombre_de_variable,
      variableToFormValue(complemento, variable),
    ];
  }).filter(([name]) => name);
  const complementosTable = complementos.filter((complemento) => {
    const variable = catalogoDeVariables.find((v) => v.id === complemento.variable);
    if (variable.tipo_de_variable === tiposDatosEnum.TABLA) return true;
    return false;
  });
  const variablesTable = getDistinctValues(complementosTable.map((c) => c.variable));

  const tableEntries = variablesTable.map((_variable) => {
    const variable = catalogoDeVariables.find((v) => v.id === _variable);
    const _complementos = complementos.filter((c) => c.variable === _variable);
    return [
      variable.nombre_de_variable,
      _complementos.map((c) => ({
        ...complementosToValues(JSON.parse(c.json).complementos, catalogoDeVariables),
        id: c.id,
      })),
    ];
  }).filter(([name]) => name);

  return { ...Object.fromEntries(entries), ...Object.fromEntries(tableEntries) };
};

const getControl = (
  {
    tipo_de_variable: tipo,
    descripcion_de_variable,
    id: idVariable,
  },
  disabled,
  valoresDeVariablesDeSelecion,
  dataSourcesRef,
  catalogosObtenidos,
  nombre_de_variable,
  required,
  cantidad_decimales,
) => {
  if (tipo.tipo_de_variable === tiposDatosEnum.TABLA) return <Select />;
  if ([tiposDatosEnum.RANGO_DE_FECHAS, tiposDatosFormulaEnum.RANGO_DE_FECHAS].includes(tipo)) {
    return (<DatePicker.RangePicker disabled={disabled} />);
  }
  if (tipo === tiposDatosEnum.FECHA) {
    return (<DatePicker disabled={disabled} format={DATE_FRONTEND_FORMAT} />);
  }
  if (tipo === tiposDatosEnum.FECHA_HORA) {
    return (<DatePicker showTime disabled={disabled} format={DATETIME_FRONTEND_FORMAT} />);
  }
  if (tipo === tiposDatosEnum.BOOLEAN) {
    return (<Select trueFalse disabled={disabled} />);
  }
  if ([tiposDatosEnum.SELECCION_UNICA, tiposDatosEnum.SELECCION_MULTIPLE].includes(tipo)) {
    const valores = valoresDeVariablesDeSelecion.filter((val) => val.variable === idVariable)
      .map((e) => ({ ...e, id_valor: `${e.id}__${e.valor}` }));
    return (
      <Select
        dataSource={dataSourcesRef.current[nombre_de_variable] || valores}
        mode={tipo === tiposDatosEnum.SELECCION_MULTIPLE ? 'multiple' : undefined}
        labelProp="valor"
        valueProp="id_valor"
        disabled={disabled || !catalogosObtenidos}
      />
    );
  }
  if (tiposDatosTexto.includes(tipo)) {
    return (<Input allowClear disabled={disabled} />);
  }
  if (tiposDatosNumericos.includes(tipo)) {
    const decimal = [tiposDatosEnum.DECIMAL, tiposDatosFormulaEnum.DECIMAL].includes(tipo);
    return (
      <NumericInput
        label={descripcion_de_variable?.replace(/:$/g, '')}
        name={nombre_de_variable}
        decimal={decimal}
        precision={cantidad_decimales}
        required={required}
        disabled={disabled}
        commas
      />
    );
  }
  return (<Input placeholder="Tipo de dato no soportado" disabled />);
};

export const permissionVariablesGPM = {
  model: 'complementodetramite',
  catalogs: [
    'variable',
    'valorporvariabledeseleccion',
    'categoriadevariables',
  ],
};

const getParams = (paramsString, ctx) => {
  if (!paramsString) return null;
  const entries = paramsString.split('&').map((param) => {
    const [k, value] = param.split('=');
    // extract value from context
    if (/^[a-z]{1,}=(ENTIDAD|TRAMITE|USUARIO)__[a-z_]{1,}$/.test(param)) {
      const [ctxKey, key] = value.split('__');
      return [k, ctx[ctxKey][key]];
    }
    return [k, value];
  });
  return Object.fromEntries(entries);
};

const Variables = ({
  variables: _variables,
  catalogoDeVariables,
  disabled,
  prename,
  valoresDeVariablesDeSelecion,
  onClick,
  categoriasDeVariables,
  required: _required,
  tramite: TRAMITE,
  valuesVariablesCargos,
  showTotal,
  fetchComplementos,
}) => {
  const formInstance = Form.useFormInstance();
  const [formModal] = Form.useForm();
  const moneda = useSelector(({ auth }) => auth.entidad.moneda);
  const dataSourcesRef = useRef({});
  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState({});
  const { entidad: ENTIDAD, user: USUARIO } = useSelector((s) => s.auth);
  const [catalogosObtenidos, setCatalogosObtenidos] = useState(false);

  const variables = useMemo(
    () => catalogoDeVariables.filter((v) => _variables.includes(v.id)),
    [_variables, catalogoDeVariables],
  );

  const tables = useMemo(
    () => variables.filter((v) => v.tipo_de_variable === tiposDatosEnum.TABLA),
    [variables],
  );

  const visibleFormVariables = useMemo(
    () => variables.filter((v) => v.variable === visibleModal),
    [visibleModal],
  );

  const get = async (url, params, label) => {
    try {
      const response = await HTTP.get(url, { params });
      return response.data.map((e) => {
        const valor = label.split('__').map((l) => e[l]).join(' ').trim()
          .toUpperCase();
        return { ...e, id_valor: `${e.id}__${valor}`, valor };
      });
    } catch (err) {
      return [];
    }
  };

  useEffect(() => {
    const ctx = { ENTIDAD, TRAMITE, USUARIO };
    const fetchCatalogos = async () => {
      const variablesCatalogos = variables.filter((v) => v.es_catalogo_interno);
      const promises = variablesCatalogos.map((v) => get(v.url, getParams(v.params, ctx), v.label));
      const responses = await Promise.all(promises);
      variablesCatalogos.forEach(({ nombre_de_variable }, idx) => {
        dataSourcesRef.current[nombre_de_variable] = responses[idx];
      });
      if (showTotal) {
        setTimeout(() => {
          const { PORCENTAJE, base_gravable } = formInstance.getFieldsValue()[prename] || {};
          const total = (parseFloat(base_gravable) * parseFloat(PORCENTAJE / 100))
            .toFixed(moneda.decimales);
          formInstance.setFieldValue([prename, '__total'], total);
        });
      }
      setCatalogosObtenidos(true);
    };

    fetchCatalogos();
  }, []);

  const shouldUpdate = (prev, curr, nombre_de_variable) => {
    const preVal = prename ? prev[prename]?.[nombre_de_variable] : prev[nombre_de_variable];
    const currVal = prename ? curr[prename]?.[nombre_de_variable] : curr[nombre_de_variable];
    return preVal !== currVal;
  };

  const getMd = (variable, modal) => {
    if (variable.tipo_de_variable === tiposDatosEnum.TABLA) return 0;
    if (modal) return 24;
    return [tiposDatosEnum.TEXTO_LARGO, tiposDatosEnum.SELECCION_MULTIPLE]
      .includes(variable.tipo_de_variable) ? 16 : 8;
  };

  const getPixelsByMd = (md) => {
    if (md === 8) return 200;
    if (md === 16) return 300;
    if (md === 24) return 400;
    return 0;
  };

  const renderVariable = (variable, modal = false) => (
    <Col
      xs={variable.tipo_de_variable !== tiposDatosEnum.TABLA ? 24 : 0}
      sm={variable.tipo_de_variable !== tiposDatosEnum.TABLA ? 24 : 0}
      md={getMd(variable, modal)}
      key={variable.id}
    >
      <Form.Item noStyle shouldUpdate={(p, c) => shouldUpdate(p, c, variable.nombre_de_variable)}>
        {() => {
          const nombre_de_variable = prename
            ? [prename, variable.nombre_de_variable] : variable.nombre_de_variable;
          const _disabled = disabled || (prename
            && valuesVariablesCargos?.[prename]?.[variable.nombre_de_variable] !== undefined);
          const rules = variable.nombre_de_variable === 'totalizado_de_cobros' ? []
            : [{ required: prename || _required || variable.es_requerido }];
          if (tiposDatosNumericos.includes(variable.tipo_de_variable)) {
            return getControl(
              variable,
              _disabled,
              valoresDeVariablesDeSelecion,
              dataSourcesRef,
              catalogosObtenidos,
              nombre_de_variable,
              prename || _required || variable.es_requerido,
              variable.cantidad_decimales || ENTIDAD.moneda?.decimales,
            );
          }
          return (
            <FormItem
              label={variable.descripcion_de_variable?.replace(/:$/g, '')}
              name={prename ? [prename, variable.nombre_de_variable] : variable.nombre_de_variable}
              rules={rules}
              normalize={tiposDatosTexto.includes(variable.tipo_de_variable) ? capitalize : null}
            >
              {getControl(
                variable,
                _disabled,
                valoresDeVariablesDeSelecion,
                dataSourcesRef,
                catalogosObtenidos,
                nombre_de_variable,
              )}
            </FormItem>
          );
        }}
      </Form.Item>
    </Col>
  );

  const categories = categoriasDeVariables.map((c) => ({
    id: c.id,
    title: c.descripcion,
    variables: variables.filter((v) => v.categoria_de_variable === c.id),
  })).filter((c) => c.variables.length);

  useEffect(() => {
    if (disabled && onClick) onClick(false);
  }, [disabled]);

  const resetSelectedRowKeys = (_visibleModal = visibleModal, _visibleAlert = visibleAlert) => {
    const _selectedRowKeys = { ...selectedRowKeys };
    if (_visibleModal) {
      _selectedRowKeys[_visibleModal] = [];
    }
    if (_visibleAlert) {
      _selectedRowKeys[_visibleAlert] = [];
    }
    setSelectedRowKeys(_selectedRowKeys);
  };

  const onCancel = () => {
    setVisibleAlert(false);
    setVisibleModal(false);
    formModal.resetFields();
    resetSelectedRowKeys();
  };

  const onFinish = async (formValues) => {
    const [key] = selectedRowKeys[visibleModal] || [];
    setLoading(true);
    const complementos = valuesToVariablesComplementos(
      formValues,
      visibleFormVariables.map((e) => e.id),
      catalogoDeVariables,
      TRAMITE.id,
    );
    const entriesValor = complementos.map((c) => {
      const variable = visibleFormVariables.find((v) => v.id === c.variable);
      return [variable.nombre_de_variable, c.valor];
    });
    const entrieJson = complementos.map((c) => {
      const variable = visibleFormVariables.find((v) => v.id === c.variable);
      return [
        variable.nombre_de_variable,
        tiposDatosSeleccion.includes(variable.tipo_de_variable)
          ? c.valores_por_variables_de_seleccion : formValues[variable.nombre_de_variable],
      ];
    });
    const valor = JSON.stringify(Object.fromEntries(entriesValor));
    const fulldateFields = visibleFormVariables
      .filter((v) => v.tipo_de_variable === tiposDatosEnum.FECHA_HORA)
      .map((v) => v.nombre_de_variable);
    const json = formatValues(
      Object.fromEntries(entrieJson),
      {},
      fulldateFields.length ? fulldateFields : false,
    );
    const values = {
      variable: visibleModal,
      tramite: TRAMITE.id,
      json: JSON.stringify({ json, complementos }),
      valor,
      ids_catalogo: '',
      valores_por_variables_de_seleccion: [],
    };

    const saved = await (key
      ? patchComplementoDeTramite(key, values) : postComplementoDeTramite(values));
    if (saved) {
      onCancel();
      await fetchComplementos();
    }
    setLoading(false);
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys[visibleAlert] || [];
    const deleted = await deleteComplementoDeTramite(key);
    if (deleted) {
      await fetchComplementos();
      onCancel();
    }
    setLoading(false);
  };

  if (!catalogosObtenidos) return 'Obteniendo catálogos ...';

  const titleSelected = variables
    .find((v) => v.id === (visibleAlert || visibleModal))?.descripcion_de_variable;

  const getDisplayValue = (variable, valor) => {
    const [id] = valor.split('__');
    if (variable.es_catalogo_interno) {
      return dataSourcesRef.current[variable.nombre_de_variable]
        .find((e) => e.id === id)?.descripcion;
    }
    return valoresDeVariablesDeSelecion.find((vvs) => vvs.id.toString() === id)?.valor;
  };

  return (
    <>
      {categories.map((c) => (
        <React.Fragment key={c.id}>
          <Typography.Title level={3} style={{ width: '100%' }}>
            {c.title}
          </Typography.Title>
          {c.variables.filter((v) => !v.variable)
            .map(renderVariable)}
          <Col span={24} />
          <br />
        </React.Fragment>
      ))}
      {variables.filter((v) => !v.variable && !v.categoria_de_variable).map(renderVariable)}
      {showTotal && (
        <Col xs={24} sm={24} md={8}>
          <NumericInput
            label={capitalize('Total')}
            name={[prename, '__total']}
            precision={moneda.decimales}
            disabled
            decimal
            commas
          />
        </Col>
      )}
      {!!tables.length && (
        <>
          {tables.map((t) => {
            const cols = variables.filter((v) => v.variable === t.id).map((variable) => ({
              titleText: variable.descripcion_de_variable,
              key: variable.nombre_de_variable,
              dataIndex: variable.nombre_de_variable,
              width: getPixelsByMd(getMd(variable)),
              render: (val) => {
                if (variable.tipo_de_variable === tiposDatosEnum.FECHA) {
                  return val.format(DATE_FRONTEND_FORMAT);
                }
                if (variable.tipo_de_variable === tiposDatosEnum.FECHA_HORA) {
                  return val.format(DATETIME_FRONTEND_FORMAT);
                }
                if (variable.tipo_de_variable === tiposDatosEnum.RANGO_DE_FECHAS) {
                  return val.map((e) => e.format(DATE_FRONTEND_FORMAT)).join(' - ');
                }
                if (variable.tipo_de_variable === tiposDatosEnum.BOOLEAN) return val ? 'Sí' : 'No';
                if (variable.tipo_de_variable === tiposDatosEnum.SELECCION_UNICA) {
                  return getDisplayValue(variable, val);
                }
                if (variable.tipo_de_variable === tiposDatosEnum.SELECCION_MULTIPLE) {
                  return listToString(val.map((_val) => getDisplayValue(variable, _val)));
                }
                return val;
              },
            }));

            const data = formInstance.getFieldValue(t.nombre_de_variable);
            return (
              <Col span={24} key={t.id}>
                <Typography.Title level={3}>
                  {t.descripcion_de_variable}
                </Typography.Title>
                <Table
                  cols={cols}
                  data={data || []}
                  rowSelection={disabled ? null : {
                    selectedRowKeys: selectedRowKeys[t.id] || [],
                    type: 'radio',
                  }}
                  handleOnRowClick={(record) => {
                    setSelectedRowKeys({
                      ...selectedRowKeys,
                      [t.id]: [record.id],
                    });
                  }}
                  controls={{
                    onClickAdd: () => {
                      resetSelectedRowKeys(t.id);
                      setVisibleModal(t.id);
                    },
                    onClickDelete: () => setVisibleAlert(t.id),
                    onClickEdit: () => {
                      const [key] = selectedRowKeys[t.id] || [];
                      const match = data.find((e) => e.id === key);
                      formModal.setFieldsValue(match);
                      setVisibleModal(t.id);
                    },
                  }}
                  noControls={disabled}
                  allowSort={false}
                  allowSearch={false}
                  allowColumnSelector={false}
                  rowKey="id"
                  mobileColIndex={0}
                  permission={{
                    hasAllViewPermission: true,
                    view: true,
                    add: true,
                    change: true,
                    delete: true,
                  }}
                />
              </Col>
            );
          })}
          <Modal
            open={!!visibleModal}
            closable={false}
            footer={null}
            title={(
              <FormSubmitControls
                label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${titleSelected}`}
                onFinish={formModal.submit}
                onCancel={onCancel}
                loading={loading}
              />
            )}
            keyboard={!loading}
            maskClosable={!loading}
            onCancel={onCancel}
            destroyOnClose
          >
            <Spin spinning={loading}>
              <Form form={formModal} layout="vertical" onFinish={onFinish}>
                <Row gutter={10}>{visibleFormVariables.map((v) => renderVariable(v, true))}</Row>
              </Form>
            </Spin>
          </Modal>
          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => setVisibleAlert(false)}
            visible={!!visibleAlert}
            content={`${titleSelected} seleccionado`}
          />
        </>
      )}
      {!!onClick && disabled && (
        <>
          <Col span={24} />
          <br />
          <Col xs={24} sm={24} md={{ span: 8, push: 16 }}>
            <Button onClick={() => onClick(true)} block>
              Imprimir
            </Button>
          </Col>
          <br />
        </>
      )}
    </>
  );
};

Variables.propTypes = {
  catalogoDeVariables: PropTypes.arrayOf(PropTypes.shape({
    tipo_de_variable: PropTypes.number.isRequired,
    nombre_de_variable: PropTypes.string.isRequired,
    descripcion_de_variable: PropTypes.string.isRequired,
  })),
  variables: PropTypes.arrayOf(PropTypes.number),
  categoriasDeVariables: PropTypes.arrayOf(PropTypes.shape),
  valoresDeVariablesDeSelecion: PropTypes.arrayOf(PropTypes.shape({})),
  prename: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  required: PropTypes.bool,
  tramite: PropTypes.shape(),
  valuesVariablesCargos: PropTypes.shape(),
  showTotal: PropTypes.bool,
  fetchComplementos: PropTypes.func,
};

Variables.defaultProps = {
  prename: null,
  disabled: false,
  onClick: null,
  required: false,
  catalogoDeVariables: [],
  variables: [],
  categoriasDeVariables: [],
  valoresDeVariablesDeSelecion: [],
  tramite: null,
  valuesVariablesCargos: null,
  showTotal: false,
  fetchComplementos: null,
};

const FormItem = styled(Form.Item)`
  label {
    height: auto;
  }
`;

export default Variables;
